import type {
  WebSocketFactory,
  WebSocketLike,
  WpsCloseEvent,
  WpsMessageEvent,
} from '@github-ui/ahp-relay/webpubsub/wps-transport'
import {isFeatureEnabled} from '@github-ui/feature-flags'

/**
 * - `ahp`: transport and protocol traffic (relay, WebSocket, Mission Control HTTP).
 * - `user`: intentional user actions that should lead to AHP traffic.
 * - `browser`: uncaught errors and unhandled promise rejections on the page.
 */
export type AhpDebugEventCategory = 'ahp' | 'user' | 'browser'

export interface AhpDebugEvent {
  id: string
  timestamp: string
  category: AhpDebugEventCategory
  source: 'runtime' | 'mission-control' | 'relay' | 'websocket' | 'composer' | 'window'
  direction: 'inbound' | 'outbound' | 'status' | 'action' | 'error'
  message: string
}

export type AhpDebugEventInput = Omit<AhpDebugEvent, 'id' | 'timestamp' | 'category'> & {
  category?: AhpDebugEventCategory
}

export function isAhpDebugLogEnabled(): boolean {
  return isFeatureEnabled('one_copilot_ahp_debug_log')
}

let nextDebugEventId = 1

export function createDebugEvent(event: AhpDebugEventInput): AhpDebugEvent {
  const id = `debug-${nextDebugEventId}`
  nextDebugEventId += 1
  return {
    id,
    timestamp: new Date().toISOString(),
    ...event,
    category: event.category ?? 'ahp',
  }
}

const PROMPT_PREVIEW_LENGTH = 80

export interface DebugPromptSummary {
  textLength: number
  textPreview?: string
  attachments?: string[]
}

/**
 * Summarizes a prompt for the debug log. The full text already appears in the
 * outbound AHP frames, so a short preview is enough to line the action up with
 * the protocol traffic it produced.
 */
export function summarizePromptForDebugLog(
  text: string,
  attachments?: ReadonlyArray<{type?: string}>,
): DebugPromptSummary {
  const singleLine = text.replace(/\s+/g, ' ').trim()
  return {
    textLength: text.length,
    ...(singleLine
      ? {
          textPreview:
            singleLine.length > PROMPT_PREVIEW_LENGTH ? `${singleLine.slice(0, PROMPT_PREVIEW_LENGTH)}…` : singleLine,
        }
      : {}),
    ...(attachments && attachments.length > 0
      ? {attachments: attachments.map(attachment => attachment.type ?? 'unknown')}
      : {}),
  }
}

export function createUserActionDebugEvent(action: string, details?: object): AhpDebugEventInput {
  return {
    category: 'user',
    source: 'composer',
    direction: 'action',
    message: JSON.stringify({action, ...details}),
  }
}

export function createBrowserErrorDebugEvent(event: ErrorEvent | PromiseRejectionEvent): AhpDebugEventInput {
  let details: Record<string, unknown>
  if ('reason' in event) {
    details = {type: 'unhandledrejection', ...describeError(event.reason)}
  } else {
    const error = describeError(event.error)
    details = {
      type: 'error',
      ...error,
      // `event.message` is the browser's own rendering and stays available
      // for cross-origin script errors, where `event.error` is null.
      message: event.message || error.message,
      ...(event.filename ? {location: `${event.filename}:${event.lineno}:${event.colno}`} : {}),
    }
  }
  return {category: 'browser', source: 'window', direction: 'error', message: JSON.stringify(details)}
}

function describeError(error: unknown): {name?: string; message: string; stack?: string} {
  if (error instanceof Error) {
    return {name: error.name, message: error.message, ...(error.stack ? {stack: error.stack} : {})}
  }
  if (typeof error === 'string') return {message: error}
  try {
    return {message: JSON.stringify(error) ?? String(error)}
  } catch {
    return {message: String(error)}
  }
}

export function formatDebugEvent(event: AhpDebugEvent): string {
  return `[${event.timestamp}] [${event.category}] ${event.source}/${event.direction} ${event.message}`
}

export function formatDebugLog(events: readonly AhpDebugEvent[]): string {
  return events.map(formatDebugEvent).join('\n')
}

export function downloadDebugLog(debugLog: string, fileName = 'copilot-debug.log'): void {
  const blob = new Blob([debugLog], {type: 'text/plain;charset=utf-8'})
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')

  try {
    anchor.href = url
    anchor.download = fileName
    anchor.style.display = 'none'
    document.body.appendChild(anchor)
    anchor.click()
  } finally {
    anchor.remove()
    setTimeout(() => URL.revokeObjectURL(url), 0)
  }
}

function debugFrameToString(data: unknown): string {
  if (typeof data === 'string') return data
  if (data instanceof ArrayBuffer) return new TextDecoder('utf-8').decode(data)
  if (ArrayBuffer.isView(data)) return new TextDecoder('utf-8').decode(data)
  return String(data)
}

function debugUrlToString(url: string): string {
  try {
    const baseUrl = typeof window === 'undefined' ? 'http://localhost' : window.location.origin
    const parsedUrl = new URL(url, baseUrl)
    parsedUrl.search = ''
    return parsedUrl.toString()
  } catch {
    return url.replace(/([?&]access_token=)[^&#]*/gi, '$1[REDACTED]')
  }
}

export function createDebugWebSocketFactory(onDebugEvent: (event: AhpDebugEventInput) => void): WebSocketFactory {
  return (url, subprotocol) => {
    onDebugEvent({source: 'websocket', direction: 'status', message: `connecting ${debugUrlToString(url)}`})
    const socket = new WebSocket(url, subprotocol) as unknown as WebSocketLike
    let onopen: (() => void) | null = null
    let onmessage: ((event: WpsMessageEvent) => void) | null = null
    let onclose: ((event: WpsCloseEvent) => void) | null = null
    let onerror: ((event: unknown) => void) | null = null

    socket.onopen = () => {
      onDebugEvent({source: 'websocket', direction: 'status', message: 'open'})
      onopen?.()
    }
    socket.onmessage = event => {
      onDebugEvent({
        source: 'websocket',
        direction: 'inbound',
        message: debugFrameToString(event.data),
      })
      onmessage?.(event)
    }
    socket.onclose = event => {
      onDebugEvent({
        source: 'websocket',
        direction: 'status',
        message: `close code=${event.code} reason=${event.reason}`,
      })
      onclose?.(event)
    }
    socket.onerror = event => {
      onDebugEvent({
        source: 'websocket',
        direction: 'status',
        message: `error ${String(event)}`,
      })
      onerror?.(event)
    }

    return {
      send(data: string) {
        onDebugEvent({source: 'websocket', direction: 'outbound', message: data})
        socket.send(data)
      },
      close() {
        onDebugEvent({source: 'websocket', direction: 'status', message: 'close requested'})
        socket.close()
      },
      get onopen() {
        return onopen
      },
      set onopen(value) {
        onopen = value
      },
      get onmessage() {
        return onmessage
      },
      set onmessage(value) {
        onmessage = value
      },
      get onclose() {
        return onclose
      },
      set onclose(value) {
        onclose = value
      },
      get onerror() {
        return onerror
      },
      set onerror(value) {
        onerror = value
      },
    }
  }
}
